/**
 * Kostnadsfunktioner för stödtjänster (FCR-D) och effekttariff.
 *
 * Båda är "ovanpålägg" — de räknas separat från den vanliga energikostnaden
 * och adderas till totalresultatet. De har sina egna verifieringstester och
 * är medvetet enkla att kontrollera manuellt.
 */

import type {
  AncillaryServiceConfig,
  BatteryConfig,
  GridFeeConfig,
  ResultHour,
  TaxConfig,
} from "./types";

/**
 * Beräkna årligt genomsnittligt intäktsbidrag från stödtjänster över
 * en vald tidshorisont.
 *
 * Modell:
 *   Intäkt år 1 = annualRevenuePerKW * batteriets maxEffekt * (1 - aggregatorShare)
 *   Intäkt år n = år 1 * annualPriceMultiplier^(n-1)
 *   Total = summa över horisonten
 *   Annualiserat = total / horisontår
 *
 * VARNING: Att lägga ihop intäkter över flera år och dividera med åren är
 * EJ samma som nuvärdesberäkning (NPV). Det är ett rimligt enkelt sätt att
 * presentera "förväntad årlig intäkt över X år" utan att blanda in
 * diskonteringsräntor. Användaren kan tolka det som en linjär medelintäkt.
 *
 * @returns Genomsnittlig årsintäkt över tidshorisonten (kr/år, netto efter
 * aggregator). Returnerar 0 om enabled = false.
 */
export function calculateAncillaryRevenue(
  config: AncillaryServiceConfig,
  battery: BatteryConfig,
): { netAnnualAverage: number; year1Revenue: number; yearByYear: number[] } {
  if (!config.enabled || config.horizonYears < 1) {
    return { netAnnualAverage: 0, year1Revenue: 0, yearByYear: [] };
  }

  const year1Gross = config.annualRevenuePerKW * battery.maxPowerKW;
  const year1Net = year1Gross * (1 - config.aggregatorShare);

  const yearByYear: number[] = [];
  let cumulative = 0;
  for (let y = 1; y <= config.horizonYears; y++) {
    const yearRev = year1Net * Math.pow(config.annualPriceMultiplier, y - 1);
    yearByYear.push(yearRev);
    cumulative += yearRev;
  }
  const netAnnualAverage = cumulative / config.horizonYears;

  return { netAnnualAverage, year1Revenue: year1Net, yearByYear };
}

/**
 * Beräkna årlig effekttariff-kostnad baserat på ResultHour-data.
 *
 * Modell:
 *   För varje månad i datan:
 *     1. Identifiera N högsta timmedeleffekter (importKWh, eftersom 1h = 1 kWh = effekt-medel)
 *     2. Snitta dessa N toppar
 *     3. Multiplicera med pricePerKWPerMonth
 *     4. Om säsongsdifferentierad: multiplicera med winterMultiplier under vintermånader
 *
 * Returnerar TOTAL kostnad över datan (inte annualiserat).
 *
 * @param monthsCovered antal månader i datan (för annualisering)
 * @returns Effekttariff i kr för hela perioden, plus per månad
 */
export function calculatePowerTariff(
  hours: ResultHour[],
  grid: GridFeeConfig,
  tax: TaxConfig,
): { totalSek: number; byMonth: Map<string, number> } {
  const empty = { totalSek: 0, byMonth: new Map<string, number>() };
  if (!grid.powerTariff) return empty;

  // Om tariffen aktiveras i framtiden, räknar vi noll just nu
  // (UI-lagret tolkar fram år för år eftersom DP-perioden är ett år).
  if (grid.powerTariff.activateFromYear && grid.powerTariff.activateFromYear > 0) {
    return empty;
  }

  const pt = grid.powerTariff;
  const N = Math.max(1, pt.topHoursAveraged);

  // Gruppera per månad (YYYY-MM)
  const byMonth = new Map<string, number[]>();
  for (const h of hours) {
    const monthKey = h.from.slice(0, 7);
    if (!byMonth.has(monthKey)) byMonth.set(monthKey, []);
    byMonth.get(monthKey)!.push(h.importKWh);
  }

  let totalSek = 0;
  const monthCosts = new Map<string, number>();

  for (const [monthKey, imports] of byMonth) {
    // Topp-N timmar
    const sorted = [...imports].sort((a, b) => b - a);
    const topN = sorted.slice(0, N);
    const avgTopKW = topN.reduce((s, v) => s + v, 0) / topN.length;

    // Säsongsmultiplikator
    let monthMultiplier = 1;
    if (pt.seasonalDifferentiation) {
      const month = parseInt(monthKey.slice(5, 7), 10);
      if (pt.seasonalDifferentiation.winterMonths.includes(month)) {
        monthMultiplier = pt.seasonalDifferentiation.winterMultiplier;
      }
    }

    const costExVat = avgTopKW * pt.pricePerKWPerMonth * monthMultiplier;
    const costInkVat = costExVat * (1 + tax.vatRate);
    monthCosts.set(monthKey, costInkVat);
    totalSek += costInkVat;
  }

  return { totalSek, byMonth: monthCosts };
}
